import { useState } from 'react'
import { useAuth }  from '@/hooks/useAuth'

function ForgeMark() {
  return (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" aria-hidden="true">
      <polygon points="26,4 48,44 4,44" stroke="var(--ember)" strokeWidth="2" fill="none" />
      <polygon points="26,14 40,42 12,42" stroke="var(--amber)" strokeWidth="1" fill="none" opacity="0.4" />
      <circle cx="26" cy="26" r="3.5" fill="var(--ember)" />
    </svg>
  )
}

function GitHubMark() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

export function AuthGate() {
  const { signInWithGitHub } = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  const handleSignIn = async () => {
    setIsLoading(true)
    await signInWithGitHub()
    // OAuth redirect takes over; no setIsLoading(false) needed
  }

  return (
    <div className="min-h-screen bg-forge-void flex items-center justify-center px-4">

      {/* Subtle dot-grid background */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: 'radial-gradient(circle, var(--ember) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }}
      />

      {/* Ambient ember glow */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-forge-ember opacity-[0.04] blur-3xl pointer-events-none" />

      {/* Card */}
      <div className="relative w-full max-w-sm">
        {/* Card glow ring */}
        <div className="absolute -inset-px rounded-xl bg-gradient-to-b from-forge-ember/25 to-transparent" />

        <div className="relative bg-forge-basalt rounded-xl p-10 shadow-2xl border border-forge-border/50">

          {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <ForgeMark />
            <h1 className="mt-5 text-2xl font-display font-bold tracking-[0.25em] text-forge-gold uppercase text-shadow-gold">
              ExMarked
            </h1>
            <p className="mt-2 text-[11px] text-forge-ash tracking-[0.2em] uppercase">
              The Forge Document Vault
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex-1 h-px bg-forge-border" />
            <span className="text-[10px] text-forge-ash tracking-[0.15em] uppercase">Access Protocol</span>
            <div className="flex-1 h-px bg-forge-border" />
          </div>

          {/* CTA */}
          <button
            onClick={handleSignIn}
            disabled={isLoading}
            className="
              w-full flex items-center justify-center gap-3
              py-3.5 rounded-lg font-semibold text-sm tracking-wider uppercase
              bg-forge-ember hover:bg-forge-molten
              text-white
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forge-ember
              focus-visible:ring-offset-2 focus-visible:ring-offset-forge-basalt
              shadow-lg shadow-forge-ember/25
            "
          >
            {isLoading ? <Spinner /> : <GitHubMark />}
            {isLoading ? 'Connecting…' : 'Sign in with GitHub'}
          </button>

          {/* Fine print */}
          <p className="mt-8 text-center text-[11px] text-forge-ash leading-relaxed">
            GitHub credentials are never stored.
            <br />
            Authentication is secured via OAuth 2.0.
          </p>
        </div>
      </div>
    </div>
  )
}
